import CloseIcon from '@mui/icons-material/Close';
import WrapTextIcon from '@mui/icons-material/WrapText';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { removeJpFromSymbols } from '../../utils/common';
import { useDesktopWidth } from '../../utils/hooks/useDesktopWidth';
import useLocalize from '../../utils/hooks/useLocalize';
import { newsProvider } from '../../utils/newsProvider';
import RelatedSymbols from '../news/RelatedSymbols';
import DateTime from './DateTime';
import DialogModal from './DialogModal';
import DialogNavigationPosition from './DialogNavigationPosition';


const useRelatedSymbols = (relatedSymbols) => {
  return removeJpFromSymbols(JSON.parse(relatedSymbols || '[]'));
};

const useStyles = makeStyles({
  articleBody: {
    fontFamily: 'monospace',
    fontSize: '14px'
  },
  articleNavigationCloseButton: {
    float: 'right'
  },
  articleHeader: {
    height: '32px'
  },
  articleTitle: {
    fontWeight: 'bold !important',
    fontSize: '1rem !important',
    borderBottom: '1px solid #bbb'
  },
  articleTimestamp: {
    display: 'inline-block',
    position: 'relative',
    top: '50%',
    transform: 'translateY(-50%)'
  },
  articleProvider: {
    float: 'right',
    backgroundColor: '#9e9e9e',
    paddingTop: '6px',
    paddingBottom: '6px',
    paddingLeft: '7px',
    paddingRight: '7px',
    fontSize: '0.675rem',
    color: 'white'
  },
  wrap: {
    whiteSpace: 'pre-wrap'
  },
  nowrap: {
    paddingTop: '15px',
    overflowX: 'scroll',
    whiteSpace: 'pre'
  },
  dialog: {
    width: '100%',
    maxWidth: '1500px',
    height: '80vh'
  },
  desktopDialog: {
    width: '100%',
    maxWidth: '1500px'
  }
});

/**
 * NewsArticleModal - Reusable modal component for displaying news articles
 *
 * @param {Object} props Component props
 * @param {boolean} props.open Whether the modal is open
 * @param {Function} props.onClose Callback when modal is closed
 * @param {Object} props.article Article object with articleId, title, timestamp, provider, relatedSymbols
 * @param {boolean} props.isWide Whether to use wide layout (affects navigation position)
 * @returns {React.Component} News article modal component
 */
const NewsArticleModal = ({ open, onClose, article, isWide = false }) => {
  const classes = useStyles();
  const localize = useLocalize();
  const isDesktopWidth = useDesktopWidth();

  // States
  const [wrap, setWrap] = useState(true);

  // Redux Store
  const fullArticle = useSelector(
    (state) => article ? (state.news.articles || {})[article.articleId] || null : null
  );

  // Callbacks
  const toggleWrap = useCallback(() => {
    setWrap(!wrap);
  }, [wrap]);

  const relatedSymbols = useRelatedSymbols(article?.relatedSymbols);

  // Don't render if no article is provided
  if (!article) {
    return null;
  }

  const isDialogFullscreen = !isWide;

  return (
    <DialogModal
      fullScreen={false}
      navigationPosition={
        isDialogFullscreen
          ? DialogNavigationPosition.Bottom
          : DialogNavigationPosition.Top
      }
      dialogClassName={isDesktopWidth ? classes.desktopDialog : classes.dialog}
      navigation={
        <div>
          <IconButton
            id={'article-navigation-wrap-button'}
            edge="start"
            color="inherit"
            onClick={toggleWrap}
            aria-label="wrap text"
            size="large"
          >
            <WrapTextIcon style={{ paddingLeft: 8 }} className={wrap ? '' : 'grey'} />
          </IconButton>
          <IconButton
            id={'article-navigation-close-button'}
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            className={classes.articleNavigationCloseButton}
            size="large"
          >
            <CloseIcon style={{ paddingRight: 8 }} />
          </IconButton>
        </div>
      }
      handleClose={onClose}
      open={open}
    >
      <Typography gutterBottom id={'article-header'} className={classes.articleHeader}>
        <div id={'article-timestamp'} className={classes.articleTimestamp}>
          <DateTime value={article.timestamp} hasZeroPad={true} />
        </div>
        <div id={'article-provider'} className={classes.articleProvider}>
          {localize(newsProvider(article.provider))}
        </div>
      </Typography>
      <Typography gutterBottom={true} id={'article-title'} className={classes.articleTitle}>
        {article.title}
      </Typography>
      <Typography gutterBottom>
        {fullArticle ? (
          <pre
            id={'article-body-pre'}
            className={[
              classes.articleBody,
              wrap ? classes.wrap : classes.nowrap
            ].join(' ')}
          >
            {fullArticle.body}
          </pre>
        ) : (
          localize('news.article.loading')
        )}
      </Typography>
      {fullArticle && <RelatedSymbols relatedSymbols={relatedSymbols} />}
    </DialogModal>
  );
};

export default NewsArticleModal;